/**
 * QC / CCTV evidence — shown inside HR, or not at all.
 *
 * The evidence used to be a plain link to `ops.shallal.me`. HR staff clicking
 * it were told "Not authenticated" (the file belongs to the QC system), while
 * anyone holding the link could have opened it outside HR.
 *
 * Now HR serves the files itself. A violation can carry several — for CCTV
 * typically a still frame AND a clip — so each one is opened separately and
 * each view is audited separately. Photos and PDFs go through the same
 * authenticated previewer used for employee documents; clips go to a player
 * that streams byte ranges, because a 3-minute clip cannot be downloaded whole
 * before it starts.
 */
import React, { useState } from 'react';
import { Eye, ImageOff, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import DocumentPreview from '@/components/documents/DocumentPreview';
import QCEvidenceVideo from '@/components/qc/QCEvidenceVideo';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const kindOf = (file) =>
  file.kind || (file.content_type?.startsWith('video/') ? 'video' : 'image');

export const QCEvidence = ({ violation, token, isRTL = false, testId = 'qc-evidence' }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const t = (en, ar) => (isRTL ? ar : en);

  if (!violation) return null;

  if (!violation.evidence_available) {
    const heldByQc = violation.evidence_state === 'held_by_qc_system';
    return (
      <div
        className="flex items-start gap-2 text-xs text-slate-500"
        data-testid={`${testId}-unavailable`}
      >
        <ImageOff className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          {heldByQc
            ? t(
                'Evidence is held in the QC system and is not available in HR yet.',
                'الدليل محفوظ في نظام الجودة ولم يصل إلى نظام الموارد البشرية بعد.'
              )
            : t('No evidence was sent with this violation.', 'لم يُرسل أي دليل مع هذه المخالفة.')}
        </span>
      </div>
    );
  }

  // Records stored before multi-file support report no list, only the one file.
  const files = violation.evidence_files?.length
    ? violation.evidence_files
    : [
        {
          index: 0,
          file_name: violation.evidence_file_name,
          content_type: violation.evidence_content_type,
        },
      ];
  const open = files.find((f) => f.index === openIndex);
  const openKind = open ? kindOf(open) : null;

  const clips = files.filter((f) => kindOf(f) === 'video').length;
  const stills = files.length - clips;
  const label = [
    stills && t(`${stills} ${stills === 1 ? 'photo' : 'photos'}`, `${stills} صورة`),
    clips && t(`${clips} ${clips === 1 ? 'clip' : 'clips'}`, `${clips} مقطع`),
  ]
    .filter(Boolean)
    .join(' · ');

  const dialogTitle = (file) => {
    const what =
      kindOf(file) === 'video'
        ? t('Violation clip', 'مقطع المخالفة')
        : t('Violation evidence', 'دليل المخالفة');
    return files.length > 1
      ? t(`${what} ${file.index + 1} of ${files.length}`, `${what} ${file.index + 1} من ${files.length}`)
      : what;
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {files.length === 1 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenIndex(0)}
            className="h-8"
            data-testid={`${testId}-view-btn`}
          >
            {kindOf(files[0]) === 'video' ? (
              <>
                <Play className="h-3.5 w-3.5 me-1.5" />
                {t('Play clip', 'تشغيل المقطع')}
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5 me-1.5" />
                {t('View evidence', 'عرض الدليل')}
              </>
            )}
          </Button>
        ) : (
          <>
            <span className="text-xs text-slate-500 me-0.5">{label}</span>
            {files.map((file) => (
              <Button
                key={file.index}
                variant="outline"
                size="sm"
                onClick={() => setOpenIndex(file.index)}
                className="h-8 px-2 gap-1 tabular-nums"
                title={file.file_name || `${file.index + 1}`}
                data-testid={`${testId}-view-btn-${file.index}`}
              >
                {kindOf(file) === 'video' ? (
                  <Play className="h-3 w-3" />
                ) : (
                  <Eye className="h-3 w-3" />
                )}
                {file.index + 1}
              </Button>
            ))}
          </>
        )}
      </div>

      {open && openKind === 'video' && (
        <QCEvidenceVideo
          violation={violation}
          index={open.index}
          title={dialogTitle(open)}
          token={token}
          isRTL={isRTL}
          onClose={() => setOpenIndex(null)}
        />
      )}

      {open && openKind !== 'video' && (
        <DocumentPreview
          open
          onClose={() => setOpenIndex(null)}
          document={{
            id: `${violation.id}-${open.index}`,
            file_name: open.file_name || 'evidence',
            mime_type: open.content_type,
            display_name:
              files.length > 1
                ? t(
                    `Evidence ${open.index + 1} of ${files.length}`,
                    `الدليل ${open.index + 1} من ${files.length}`
                  )
                : t('Violation evidence', 'دليل المخالفة'),
          }}
          downloadUrl={`${API_URL}/qc-violations/${violation.id}/evidence/${open.index}`}
          token={token}
          isRTL={isRTL}
          documentType="qc"
        />
      )}
    </>
  );
};

export default QCEvidence;
